import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Shield, Lock, FileText, Eye, AlertTriangle, Mail, Phone, Download, Upload, User, Users, Calendar, CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format, addMonths, differenceInDays } from "date-fns";
import { sortEmployeesByFullName } from "@/lib/employeeSort";

export default function HIPAADetails() {
  const [location, setLocation] = useLocation();
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const { data: employeesData = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  const employees = useMemo(() => {
    return sortEmployeesByFullName(employeesData);
  }, [employeesData]);

  // Generate HIPAA compliance data
  const hipaaData = {
    policies: {
      privacyPolicy: {
        lastUpdated: new Date('2024-01-15'),
        nextReview: new Date('2025-01-15'),
        status: 'Current'
      },
      securityPolicy: {
        lastUpdated: new Date('2024-02-01'),
        nextReview: new Date('2025-02-01'),
        status: 'Current'
      },
      breachNotification: {
        lastUpdated: new Date('2024-01-10'),
        nextReview: new Date('2025-01-10'),
        status: 'Current'
      }
    },
    training: {
      completionRate: 87,
      lastConducted: new Date('2024-03-15'),
      nextRequired: new Date('2024-09-15'),
      totalEmployees: employees.length,
      trainedEmployees: Math.floor(employees.length * 0.87)
    },
    assessments: {
      lastRiskAssessment: new Date('2024-01-01'),
      nextDue: new Date('2024-07-01'),
      vulnerabilities: 3,
      remediated: 2,
      pending: 1
    },
    breaches: [
      {
        id: 1,
        date: new Date('2024-05-15'),
        type: 'Email Misdirection',
        severity: 'Low',
        affected: 1,
        status: 'Resolved',
        reportedToOCR: false,
        description: 'Employee accidentally sent PHI to wrong email address'
      }
    ]
  };

  // Generate employee access logs
  const employeeAccessData = employees.slice(0, 15).map((employee: any) => {
    const seed = employee.id * 1357;
    const lastAccess = new Date();
    lastAccess.setDate(lastAccess.getDate() - (seed % 30));
    
    const accessLevel = ['Basic', 'Standard', 'Administrative'][seed % 3];
    const trainedDate = new Date('2024-03-15');
    const trainingCompliant = (seed % 10) !== 0; // 90% compliance
    
    return {
      ...employee,
      lastAccess,
      accessLevel,
      trainedDate,
      trainingCompliant,
      accessCount: 45 + (seed % 100),
      violations: seed % 5 === 0 ? 1 : 0
    };
  });

  const nonCompliantEmployees = employeeAccessData.filter(emp => !emp.trainingCompliant);
  const recentViolations = employeeAccessData.filter(emp => emp.violations > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/compliance?role=employer")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Compliance
          </Button>
          <h1 className="text-3xl font-bold">HIPAA Compliance Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Reports
          </Button>
          <Button size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* HIPAA Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            HIPAA Compliance Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {hipaaData.training.completionRate}%
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Training Completion
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {hipaaData.training.trainedEmployees} of {hipaaData.training.totalEmployees} employees
              </div>
            </div>
            
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                3
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Active Policies
              </div>
              <div className="text-xs text-gray-500 mt-1">
                All current and compliant
              </div>
            </div>

            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {hipaaData.assessments.pending}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Pending Remediation
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {hipaaData.assessments.remediated} resolved
              </div>
            </div>

            <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {hipaaData.breaches.length}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Reported Incidents
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Past 12 months
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="policies" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
          <TabsTrigger value="access">Access Control</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="assessments">Risk Assessment</TabsTrigger>
        </TabsList>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Privacy Policies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Privacy Policy</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.privacyPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Notice of Privacy Practices</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.privacyPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Patient Rights Policy</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.privacyPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>
                </div>

                <Button className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Create New Policy
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Security Policies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Administrative Safeguards</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.securityPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Physical Safeguards</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.securityPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Technical Safeguards</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(hipaaData.policies.securityPolicy.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>
                </div>

                <Button className="w-full">
                  <Lock className="h-4 w-4 mr-2" />
                  Update Security Rules
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Policy Review Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Review Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Policy</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Last Updated</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Next Review</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-muted/25">
                        <td className="px-4 py-3 font-medium text-sm">Privacy Policy</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.privacyPolicy.lastUpdated, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.privacyPolicy.nextReview, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3">
                          <Badge variant="default" className="text-xs">Current</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                            Review
                          </Button>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/25">
                        <td className="px-4 py-3 font-medium text-sm">Security Policy</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.securityPolicy.lastUpdated, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.securityPolicy.nextReview, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3">
                          <Badge variant="default" className="text-xs">Current</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                            Review
                          </Button>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/25">
                        <td className="px-4 py-3 font-medium text-sm">Breach Notification</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.breachNotification.lastUpdated, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3 text-sm">{format(hipaaData.policies.breachNotification.nextReview, 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3">
                          <Badge variant="default" className="text-xs">Current</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                            Review
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Training Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Overall Completion</span>
                    <span>{hipaaData.training.completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${hipaaData.training.completionRate}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border">
                    <div className="text-lg font-bold text-green-600 dark:text-green-400">
                      {hipaaData.training.trainedEmployees}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      Trained
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border">
                    <div className="text-lg font-bold text-red-600 dark:text-red-400">
                      {hipaaData.training.totalEmployees - hipaaData.training.trainedEmployees}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      Pending
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Next Training Session</Label>
                  <p className="text-sm text-muted-foreground">
                    {format(hipaaData.training.nextRequired, 'MMM dd, yyyy')}
                  </p>
                </div>

                <Button className="w-full">
                  Schedule Training Session
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Training Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Initial HIPAA Training</p>
                    <p className="text-xs text-muted-foreground">Required for all employees</p>
                  </div>
                  <Badge variant="default" className="text-xs">Required</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Annual Refresher</p>
                    <p className="text-xs text-muted-foreground">Yearly compliance update</p>
                  </div>
                  <Badge variant="default" className="text-xs">Required</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Breach Response Training</p>
                    <p className="text-xs text-muted-foreground">Incident management protocols</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Recommended</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Role-Specific Training</p>
                    <p className="text-xs text-muted-foreground">Position-based requirements</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">As Needed</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Training Status by Employee */}
          <Card>
            <CardHeader>
              <CardTitle>Employee Training Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Last Trained</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Next Due</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeAccessData.slice(0, 10).map((employee: any) => {
                        const nextDue = addMonths(employee.trainedDate, 12);
                        const statusVariant = employee.trainingCompliant ? "default" : "destructive";

                        return (
                          <tr key={employee.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{employee.firstName} {employee.lastName}</p>
                                <p className="text-xs text-muted-foreground">{employee.email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">{employee.department || 'Unassigned'}</td>
                            <td className="px-4 py-3 text-sm">
                              {employee.trainingCompliant ? format(employee.trainedDate, 'MMM dd, yyyy') : 'Never'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant} className="text-xs">
                                {employee.trainingCompliant ? 'Compliant' : 'Overdue'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {employee.trainingCompliant ? format(nextDue, 'MMM dd, yyyy') : 'Immediate'}
                            </td>
                            <td className="px-4 py-3">
                              <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                {employee.trainingCompliant ? 'Retrain' : 'Train'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Control Tab */}
        <TabsContent value="access" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Access Control & Audit Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Access Level</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Last Access</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Access Count</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Violations</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeAccessData.map((employee: any) => {
                        const accessVariant = 
                          employee.accessLevel === 'Administrative' ? 'default' :
                          employee.accessLevel === 'Standard' ? 'secondary' : 'outline';
                        
                        const statusVariant = employee.violations > 0 ? 'destructive' : 'default';

                        return (
                          <tr key={employee.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{employee.firstName} {employee.lastName}</p>
                                <p className="text-xs text-muted-foreground">{employee.department || 'Unassigned'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={accessVariant} className="text-xs">
                                {employee.accessLevel}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {format(employee.lastAccess, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3 text-sm">{employee.accessCount}</td>
                            <td className="px-4 py-3 text-sm">
                              {employee.violations > 0 ? (
                                <span className="text-red-600 font-medium">{employee.violations}</span>
                              ) : (
                                <span className="text-green-600">0</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant} className="text-xs">
                                {employee.violations > 0 ? 'At Risk' : 'Good'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                  View Log
                                </Button>
                                {employee.violations > 0 && (
                                  <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                    Review
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Incident Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Severity</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Affected Records</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">OCR Reported</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hipaaData.breaches.map((breach: any) => {
                        const severityVariant = 
                          breach.severity === 'High' ? 'destructive' :
                          breach.severity === 'Medium' ? 'secondary' : 'outline';

                        const statusVariant = breach.status === 'Resolved' ? 'default' : 'destructive';

                        return (
                          <tr key={breach.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3 text-sm">
                              {format(breach.date, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3 text-sm">{breach.type}</td>
                            <td className="px-4 py-3">
                              <Badge variant={severityVariant} className="text-xs">
                                {breach.severity}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">{breach.affected}</td>
                            <td className="px-4 py-3">
                              <Badge variant={breach.reportedToOCR ? "default" : "outline"} className="text-xs">
                                {breach.reportedToOCR ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant} className="text-xs">
                                {breach.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                View Details
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {hipaaData.breaches.length} incident(s) in the past 12 months
                </p>
                <Button>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Report New Incident
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Assessment Tab */}
        <TabsContent value="assessments" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Current Risk Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Last Assessment:</span>
                    <span className="text-sm">{format(hipaaData.assessments.lastRiskAssessment, 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Next Due:</span>
                    <span className="text-sm">{format(hipaaData.assessments.nextDue, 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Total Vulnerabilities:</span>
                    <span className="text-sm">{hipaaData.assessments.vulnerabilities}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Remediated:</span>
                    <span className="text-sm text-green-600">{hipaaData.assessments.remediated}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Pending:</span>
                    <span className="text-sm text-orange-600">{hipaaData.assessments.pending}</span>
                  </div>
                </div>

                <Button className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Start New Assessment
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Vulnerability Categories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Administrative Safeguards</p>
                    <p className="text-xs text-muted-foreground">Access controls, workforce training</p>
                  </div>
                  <Badge variant="default" className="text-xs">Low Risk</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Physical Safeguards</p>
                    <p className="text-xs text-muted-foreground">Facility access, workstation security</p>
                  </div>
                  <Badge variant="default" className="text-xs">Low Risk</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Technical Safeguards</p>
                    <p className="text-xs text-muted-foreground">Encryption, audit controls</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Medium Risk</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
